import { SystemState, VehicleScheduleState, VehicleState } from './constant';
import { CommonEnumKey, TakeOverProcessStatus } from './enum';

// -----------通用枚举-----------
/** 通用后端响应包装（code / data / message） */
export interface CommonResponse<T = any> {
  code: string | number; // 业务状态码（成功通常为 0000 或 HttpStatusCode.Success）
  data: T; // 实际数据载体
  message?: string; // 描述或错误信息
}

/** 枚举项结构 */
export interface EnumItem {
  code: string | number; // 后端返回的枚举 code，可为数字或字符串
  name: string; // 展示名称
  enable?: 1 | 0; // 是否启用，默认为 1
  parent?: any; // 父级枚举项
}

/** 共用枚举 接口 data 字段结构：key 为请求时传入的枚举 key，value 为对应枚举列表 */
export type EnumListMap = Record<CommonEnumKey, EnumItem[]>;

/** 格式化之后的枚举项结构 */
export interface FormatEnumItem {
  value: string | number; // 后端返回的枚举 code，可为数字或字符串
  label: string; // 展示名称
}
/** 共用枚举 接口 data 字段结构：key 为请求时传入的枚举 key，value 为对应枚举列表 */
export type FormatEnumListMap = Record<CommonEnumKey, FormatEnumItem[]>;

// -----------业务枚举-----------
export enum CockpitStatus {
  WORK = 'WORK',
  REST = 'REST',
  OFFLINE = 'OFFLINE',
}

export enum CockpitStatusText {
  WORK = '工作中',
  REST = '休息中',
  OFFLINE = '离线',
  REST_AFTER_ISSUE = '工单处理完成后休息',
}
export enum CockpitMode {
  LISTENING_MODE = 'LISTENING_MODE',
  WATCH_CAR_MODE = 'WATCH_CAR_MODE',
  PATROL_MODE = 'PATROL_MODE',
  FREE_MODE = 'FREE_MODE',
  MAP_COLLECTION_MODE = 'MAP_COLLECTION_MODE',
}

export enum CockpitModeText {
  LISTENING_MODE = '听单模式',
  WATCH_CAR_MODE = '盯车模式',
  PATROL_MODE = '巡查模式',
  FREE_MODE = '自由模式',
  MAP_COLLECTION_MODE = '采图模式',
}

export enum CockpitType {
  MONITOR_SEAT = 'MONITOR_SEAT', //监控座席
  COCKPIT_SEAT = 'COCKPIT_SEAT', //驾舱座席
}

export enum CockpitTypeText {
  MONITOR_SEAT = '监控', //监控座席
  COCKPIT_SEAT = '驾舱', //驾舱座席
}

export enum AcceptIssueStatus {
  ACCEPT = 'accept',
  STOP = 'stop',
}

export enum IssueType {
  OPS = 'OPS',
  PROBLEM_REPORT = 'PROBLEM_REPORT',
}

export enum IssueTypeText {
  OPS = '运维工单',
  PROBLEM_REPORT = '问题提报工单',
}

export enum IssueState {
  TO_ASSIGN = 'TO_ASSIGN',
  WAIT_ACCEPT = 'WAIT_ACCEPT',
  AUTO_CLOSE = 'AUTO_CLOSE',
  PROCESS = 'PROCESS',
  TRANSFER_WAIT_ACCEPT = 'TRANSFER_WAIT_ACCEPT',
  ON_HOLD = 'ON_HOLD',
  DISCARD_WAIT_ACCEPT = 'DISCARD_WAIT_ACCEPT',
  COMPLETE = 'COMPLETE',
  FORCE_CLOSE = 'FORCE_CLOSE',
}

export enum IssueStateText {
  TO_ASSIGN = '待分配',
  WAIT_ACCEPT = '待受理',
  AUTO_CLOSE = '自动关闭',
  PROCESS = '受理中',
  TRANSFER_WAIT_ACCEPT = '转单待受理',
  ON_HOLD = '临时挂起',
  DISCARD_WAIT_ACCEPT = '抛单待受理',
  COMPLETE = '已解决',
  FORCE_CLOSE = '强制关闭',
}

export enum TakeOverStatus {
  TAKEOVER = 'TAKEOVER',
  TEMPORARY = 'TEMPORARY',
}

export enum TakeOverStatusText {
  TAKEOVER = '急停接管',
  TEMPORARY = '临停接管',
}

export enum TakeOverSource {
  MONITOR = 'MONITOR',
  MINI_MONITOR = 'MINI_MONITOR',
  REMOTE_JOYSTICK = 'REMOTE_JOYSTICK',
  WORKBENCH_MONITOR = 'WORKBENCH_MONITOR',
}

export const TakeOverSourceText = {
  [TakeOverSource.MONITOR]: '监控',
  [TakeOverSource.MINI_MONITOR]: '小程序',
  [TakeOverSource.REMOTE_JOYSTICK]: '驾舱',
  WORKBENCH_MONITOR: '小哥工作台',
};

export enum IssueStatus {
  TO_ASSIGN = 'TO_ASSIGN',
  TRANSFER_WAIT_ACCEPT = 'TRANSFER_WAIT_ACCEPT',
  PROCESS = 'PROCESS',
  ON_HOLD = 'ON_HOLD',
  COMPLETE = 'COMPLETE',
  FORCE_CLOSE = 'FORCE_CLOSE',
  WAIT_ACCEPT = 'WAIT_ACCEPT',
  DISCARD_WAIT_ACCEPT = 'DISCARD_WAIT_ACCEPT',
  DISCARD_WAIT_ASSIGN = 'DISCARD_WAIT_ASSIGN',
  AUTO_CLOSE = 'AUTO_CLOSE',
}

export const IssueStatusText = new Map([
  [IssueStatus.TO_ASSIGN, '待分配'],
  [IssueStatus.DISCARD_WAIT_ASSIGN, '抛单待分配'],
  [IssueStatus.ON_HOLD, '临时挂起'],
  [IssueStatus.PROCESS, '受理中'],
  [IssueStatus.AUTO_CLOSE, '自动关闭'],
  [IssueStatus.COMPLETE, '已解决'],
  [IssueStatus.FORCE_CLOSE, '强制关闭'],
  ['urgent', '紧急工单'],
]);
export interface CockpitTeamInfo {
  cockpitTeamNumber: string; // 驾舱团队编号
  cockpitTeamName: string; // 驾驶团队名称
  completeIssueCount: number; // 处理工单数
  waitAcceptIssueCount: number; //待处理工单数
  workTotal: number; //工作中座舱数量
  restTotal: number; //未工作座舱数量
  offlineTotal: number; // 离线座舱数量
  cockpitList:
    | {
        cockpitNumber?: string; //驾驶舱编号
        vehicleName?: string; //接管车辆编号
        cockpitUserName?: string; //用户名
        cockpitType: CockpitType; //驾舱类型
        cockpitStatus?: CockpitStatus; // 工作状态
      }[]
    | null;
}

export interface UserInfo {
  workTimeTotal: number; // 今日历史工作时长
  workStartTime: string | null; // 当次工作开始时间
  completeIssueCount: number; //处理工单数
  cockpitMode: CockpitMode; //坐席模式
  cockpitStatus: CockpitStatus; // 坐席状态
  cockpitNumber: string; //座席编号
  lastCockpitNumber: string; //上次座席编号
  acceptIssueStatus: AcceptIssueStatus; //接单状态
  recordTime: string; //记录时间
}

export interface IssueDrawerData {
  vehicleName: string; //车号
  vehicleState: VehicleState; //车辆模式
  systemState: SystemState; //系统状态
  scheduleState: VehicleScheduleState; // 调度状态
  speed: number; //速度
  issueEventList: {
    alarmName: string; // 告警名称
    reportTime: string; // 上报时间
  }[];
  issueEventRecordList?: {
    reportTime: string; // 上报时间
    description: string; //描述信息
  }[];
  issueNumber: string; // 工单编号
  issueStatus: string; //工单状态
  issueReminder?: boolean; // 是否催单
  emergencyIssue?: boolean; // 是否紧急工单
  issueType: string; //工单类型
  issueDescription?: string; //问题描述
  issuePendingStartTime: string; // 工单挂起开始时间
  issueSource?: string; //工单来源
  issueSourceUser?: string; //工单转单，派单用户
  manualAlarmRecordAttachmentList: {
    type: string;
    fileKey: string;
    url: string;
  }[];
}

export interface VehicleLitResponse {
  vehicleName: string;
  stationName: string;
  vehicleState: VehicleState;
  scheduleState: VehicleScheduleState;
  speed: number;
  sceneList: string[]; // 场景
  systemState: SystemState;
  takeOverUserName?: string; // 接管人
  takeOverSource: TakeOverSource; // 接管来源
  takeOverStatus: TakeOverStatus; // 接管状态 临时停车还是接管
  alarmEventList: {
    alarmEvent: string;
    reportTime: string;
  }[];
  globalMileage: number; // 当前规划总里程
  arrivedMileage: number; // 当前规划已走里程
}
export interface MapVehicleListResponse {
  vehicleName: string;
  stationName: string;
  vehicleState: VehicleState;
  speed: number;
  sceneList: string[]; // 场景
  systemState: SystemState;
  takeOverUserName?: string; // 接管人
  takeOverSource?: TakeOverSource; // 接管来源
  takeOverStatus?: TakeOverStatus; // 接管状态 临时停车还是接管
  alarmEventList: {
    alarmEvent: string;
    reportTime: string;
  }[];
  globalMileage: number; // 当前规划总里程
  arrivedMileage: number; // 当前规划已走里程
  freeSize: number; // 剩余空间大小
  bindingState: boolean; // 任务绑定状态
  power: number; // 电量
  scheduleState?: VehicleScheduleState;
}
export interface TemporaryStopRequest {
  vehicleName: string;
  timeStamp: number;
  commandSource?:
    | TakeOverSource.MINI_MONITOR
    | TakeOverSource.MONITOR
    | TakeOverSource.REMOTE_JOYSTICK;
}
export interface IssueItem {
  alarmEventList: {
    alarmEvent: string;
    reportTime: string;
  }[];
  scheduleState?: string;
  vehicleName: string;
  issueState: IssueStatus;
  vehicleState?: VehicleState;
  speed?: number;
  systemState?: SystemState;
  issueProcessTime?: string;
  issueNumber: string;
  issueType: IssueType;
  issueReminder?: boolean;
}
export interface CockpitInfo {
  cockpitNumber: string;
  cockpitType: CockpitType;
  onlineUser?: string;
  workTimeTotal?: number;
  completeIssueCount?: number;
  issueList?: {
    alarmEventList: {
      alarmEvent: string;
      reportTime: string;
    }[];
    scheduleState?: string;
    issueState: IssueStatus;
    vehicleName: string;
    vehicleState?: VehicleState;
    speed?: number;
    systemState?: SystemState;
    issueProcessTime?: string;
    issueNumber: string;
    issueType: IssueType;
    issueReminder?: boolean;
  };
}

export interface LoginUserInfo {
  userInfo: {
    userName: string;
    companyName: string;
    companyNumber: string;
    appCode: string;
    appRoleInfoList: {
      roleName: string;
      roleNumber: string;
    }[];
    resourceList: {
      openType: string;
      orderBy: number;
      path: string;
      code: string;
      name: string;
      type: string;
    }[];
  } | null;
  token: string;
  mqttToken: string;
}

export interface TaskDetail {
  totalMileage: string;
  taskRouteList: LatLon[];
  fourLaneList?: {
    startAddress: string;
    endAddress: string;
    routeIndexList: number[];
  }[];
  taskName: string;
}

export interface SpecialMarker {
  markId: number;
  markType: string;
  latitude: number;
  longitude: number;
  addressName: string;
  remark: string;
  attachmentList: {
    type: string;
    fileKey: string;
    url: string;
  };
}

export interface LatLon {
  latitude: number;
  longitude: number;
}
export interface CollectRouteMapProps {
  id: string;
  latitude: number;
  longitude: number;
  taskRouteList?: LatLon[];
  fourLaneIndexList?: {
    startAddress: string;
    endAddress: string;
    routeIndexList: number[];
  }[];
  finishedRouteList?: LatLon[];
  rotation?: number;
  centerMark?: LatLon;
}
